import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  type Connection,
  ConnectionLineType,
  Controls,
  type Edge,
  MiniMap,
  type Node,
  type NodeMouseHandler,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  CheckCircle2,
  Edit,
  ExternalLink,
  EyeOff,
  Gift,
  GitBranch,
  Heart,
  Info,
  Package,
  Trash2,
  TrendingUp,
  Unplug,
  X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Checkout } from '@/@types/Checkouts/list-checkouts';
import { ProductRole } from '@/@types/Products';
import { OfferData } from '@/@types/Products/Offers';
import { Upsell } from '@/@types/Upsell';
import DialogDelete from '@/components/shared/DialogDelete/DialogDelete';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import { useFetchProductById } from '@/hooks/useFetchProductById';
import LayoutInside from '@/layout/LayoutInside';
import useModalStore from '@/store/useModalStore';

import CreateOffer from '@/pages/EditProduct/Offers/Partials/CreateOffer';
import EditOffer from '@/pages/EditProduct/Offers/Partials/EditOffer';
import { OffersService } from '@/services/offers.service';

import CreateCheckout from '@/pages/EditProduct/Checkouts/Partials/CreateCheckout';
import EditCheckout from '@/pages/EditProduct/Checkouts/Partials/EditCheckout';
import { CheckoutsService } from '@/services/checkouts.service';

import { AddOrderBumpSheet } from '@/pages/EditProduct/Orderbumps/Partials/AddOrderBumpSheet';
import { EditOrderBumpSheet } from '@/pages/EditProduct/Orderbumps/Partials/EditOrderBumpSheet';
import { OrderBump, useOrderBumps } from '@/pages/EditProduct/Orderbumps/hooks/useOrderBumps';

import { EditUpsellSheet } from '@/pages/EditProduct/Upsells/Partials/EditUpsellSheet';
import { useUpsells } from '@/pages/EditProduct/Upsells/hooks/useUpsell';


import { useFunnelData } from '@/hooks/useFunnelData';
import { AddUpsellSheet } from '../Upsells/Partials/AddUpsellSheet';
import SideQuickScript from '../Upsells/Partials/SideQuickScript';
import { ConnectionMenu, type CurrentConnection } from './components/ConnectionMenu';
import { DisconnectedUpsellsPanel } from './components/DisconnectedUpsellsPanel';
import { AnimatedEdge } from './edges/AnimatedEdge';
import { CompletionNode } from './nodes/CompletionNode';
import { ExternalRedirectNode } from './nodes/ExternalRedirectNode';
import { OrderBumpNode } from './nodes/OrderBumpNode';
import { ProductFunnelNode } from './nodes/ProductFunnelNode';
import { ThankYouNode } from './nodes/ThankYouNode';
import { UpsellNode } from './nodes/UpsellNode';
import { buildFunnelGraph } from './utils/buildFunnelGraph';
import type { FunnelData, FunnelNodeData } from './utils/funnelTypes';

const nodeTypes = {
  productFunnelNode: ProductFunnelNode,
  orderBumpNode: OrderBumpNode,
  upsellNode: UpsellNode,
  completionNode: CompletionNode,
  externalRedirectNode: ExternalRedirectNode,
  thankYouNode: ThankYouNode,
};

const edgeTypes = {
  animatedEdge: AnimatedEdge,
};

const MINIMAP_NODE_COLORS: Record<string, string> = {
  productFunnelNode: '#A9D342',
  orderBumpNode: '#F59E0B',
  upsellNode: '#10B981',
  completionNode: '#22C55E',
  externalRedirectNode: '#3B82F6',
  thankYouNode: '#8B5CF6',
};

function getMiniMapNodeColor(node: Node) {
  return MINIMAP_NODE_COLORS[node.type || ''] || '#27272a';
}

const LEGEND_KEYS = [
  { icon: Package, key: 'legend_product_offers', color: '#A9D342' },
  { icon: Gift, key: 'legend_order_bump', color: '#F59E0B' },
  { icon: TrendingUp, key: 'legend_upsell_downsell', color: '#10B981' },
  { icon: CheckCircle2, key: 'legend_purchase_completion', color: '#22C55E' },
  { icon: ExternalLink, key: 'legend_external_redirect', color: '#3B82F6' },
  { icon: Heart, key: 'legend_thank_you', color: '#8B5CF6' },
];

function enrichFunnelData(funnelData: FunnelData | null, upsellsList?: Upsell[]): FunnelData | null {
  if (!funnelData) return null;
  if (!upsellsList || upsellsList.length === 0) return funnelData;

  const enrichedUpsells = funnelData.upsells.map((fu) => {
    const fullUpsell = upsellsList.find((u) => String(u.id) === String(fu.id));
    if (!fullUpsell) return fu;
    return {
      ...fu,
      accepted_page: fullUpsell.accepted_page ?? null,
      recused_page: fullUpsell.recused_page ?? null,
    };
  });

  return {
    ...funnelData,
    upsells: enrichedUpsells,
  };
}

interface SelectedNodeInfo {
  id: string;
  type: string;
  data: FunnelNodeData;
}

export default function FunnelMap() {
  const { t } = useTranslation(['funnel', 'common', 'offers', 'checkouts', 'orderBumps', 'upsells', 'pixels']);
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { modals, openModal, closeModal } = useModalStore();
  const queryClient = useQueryClient();

  const { product, isLoadingProductDetail } = useFetchProductById(productId!);

  const { funnelData, isLoading: isLoadingFunnel, isFetching: isFetchingFunnel, invalidate: invalidateFunnel } = useFunnelData(productId!);
  const [selectedNode, setSelectedNode] = useState<SelectedNodeInfo | null>(null);
  const [showLegend, setShowLegend] = useState(true);
  const [showDisconnectedPanel, setShowDisconnectedPanel] = useState(false);

  const { data: offers } = useQuery({
    queryKey: [`offers-${productId}`, 1],
    queryFn: () => OffersService.getOffers({ productId, page: 1 }),
    staleTime: 1000 * 60 * 5,
  });

  const { data: offersOptions } = useQuery({
    queryKey: [`offers-options-${productId}`],
    queryFn: () => CheckoutsService.getOffers({ productId, page: 1 }),
    staleTime: 1000 * 60 * 5,
  });

  const { data: checkouts } = useQuery({
    queryKey: [`checkouts-${productId}`, 1],
    queryFn: () => CheckoutsService.getCheckouts({ productId, page: 1 }),
    staleTime: 1000 * 60 * 5,
  });

  const {
    orderBumps,
    createOrderBump,
    isCreating: isCreatingBump,
    deleteOrderBump,
  } = useOrderBumps({ productId: productId! });

  const {
    upsells,
    createUpsell,
    isCreating: isCreatingUpsell,
    updateUpsell,
    isUpdating: isUpdatingUpsell,
    deleteUpsell,
  } = useUpsells(productId!, { perPage: 100 });


  const [selectedOffer, setSelectedOffer] = useState<OfferData | null>(null);
  const [selectedCheckout, setSelectedCheckout] = useState<Checkout | undefined>(undefined);
  const [selectedOrderBump, setSelectedOrderBump] = useState<OrderBump | undefined>(undefined);
  const [selectedUpsell, setSelectedUpsell] = useState<Upsell | null>(null);
  const [upsellPrefilledData, setUpsellPrefilledData] = useState<{
    funnel_type?: 'up_sell' | 'down_sell';
    product_id?: string;
    offer_id?: string;
  } | null>(null);

  const enrichedFunnelData = useMemo(
    () => enrichFunnelData(funnelData, upsells?.data),
    [funnelData, upsells?.data]
  );

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => buildFunnelGraph(enrichedFunnelData),
    [enrichedFunnelData]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const { disconnectedUpsells, connectedUpsells } = useMemo(() => {
    const allUpsellsList = upsells?.data || [];
    const funnelUpsellIds = new Set(
      initialNodes
        .filter((n) => n.type === 'upsellNode')
        .map((n) => n.id.replace('upsell-', ''))
    );
    const disconnected = allUpsellsList.filter(
      (u: Upsell) => !funnelUpsellIds.has(String(u.id))
    );
    const connected = allUpsellsList.filter(
      (u: Upsell) => funnelUpsellIds.has(String(u.id))
    );
    return { disconnectedUpsells: disconnected, connectedUpsells: connected };
  }, [upsells?.data, initialNodes]);

  const handleNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    const target = _event.target as HTMLElement;
    const actionBtn = target.closest('[data-action]') as HTMLElement | null;

    if (actionBtn) {
      const action = actionBtn.getAttribute('data-action');
      const nodeType = actionBtn.getAttribute('data-node-type');
      const checkoutId = actionBtn.getAttribute('data-checkout-id');
      const offerId = actionBtn.getAttribute('data-offer-id');
      const funnelType = actionBtn.getAttribute('data-funnel-type') as 'up_sell' | 'down_sell' | null;
      const nodeData = node.data as unknown as FunnelNodeData;

      if (action === 'edit') {
        handleCTAEdit(nodeType, nodeData, offerId);
      } else if (action === 'delete') {
        handleCTADelete(nodeType, nodeData, offerId);
      } else if (action === 'edit-checkout' && checkoutId && checkouts?.data) {
        const checkout = checkouts.data.find((c: Checkout) => String(c.id) === checkoutId);
        if (checkout) {
          setSelectedCheckout(checkout);
          openModal('funnel-edit-checkout');
        }
      } else if (action === 'open-checkout' && checkoutId) {
        navigate(`/products/edit-product/${productId}/checkouts/${checkoutId}`);
      } else if (action === 'add-offer') {
        openModal('funnel-create-offer');
      } else if (action === 'add-checkout') {
        openModal('funnel-create-checkout');
      } else if (action === 'add-order-bump') {
        openModal('funnel-add-order-bump');
      } else if (action === 'add-upsell') {
        setUpsellPrefilledData(null);
        openModal('funnel-add-upsell');
      } else if (action === 'add-upsell-accept' || action === 'add-upsell-decline') {
        const upsellData = nodeData as any;
        const upsellId = actionBtn.getAttribute('data-upsell-id');
        const direction = action === 'add-upsell-accept' ? 'accept' : 'decline';
        if (upsellId) {
          setConnectionMenuState({
            upsellId,
            direction: direction as 'accept' | 'decline',
          });
        } else {
          setUpsellPrefilledData({
            funnel_type: funnelType || (action === 'add-upsell-accept' ? 'up_sell' : 'down_sell'),
            product_id: productId,
            offer_id: upsellData?.offerId,
          });
          openModal('funnel-add-upsell');
        }
      } else if (action === 'view-script') {
        const upsellId = actionBtn.getAttribute('data-upsell-id');
        if (upsellId && upsells?.data) {
          const upsell = upsells.data.find((u: Upsell) => String(u.id) === upsellId);
          if (upsell) {
            setSelectedUpsell(upsell);
            openModal('funnel-view-script');
          }
        }
      }
      return;
    }

    setSelectedNode({
      id: node.id,
      type: node.type || '',
      data: node.data as unknown as FunnelNodeData,
    });
  }, [offers, checkouts, orderBumps, upsells, navigate, productId, openModal]);

  const handleCTAEdit = (nodeType: string | null, _nodeData: FunnelNodeData, entityId?: string | null) => {
    switch (nodeType) {
      case 'offer':
        if (offers?.data && entityId) {
          const offer = offers.data.find((o: OfferData) => String(o.id) === entityId);
          if (offer) {
            setSelectedOffer(offer);
            openModal('funnel-edit-offer');
          }
        }
        break;
      case 'orderBump':
        if (orderBumps?.data && _nodeData) {
          const bump = orderBumps.data.find((b: OrderBump) => b.display_text === _nodeData.label);
          if (bump) {
            setSelectedOrderBump(bump);
            openModal('funnel-edit-order-bump');
          } else {
            navigate(`/products/edit-product/${productId}/order-bumps`);
          }
        }
        break;
      case 'upsell':
        if (upsells?.data) {
          const upsell = upsells.data.find((u: Upsell) => u.name === _nodeData.label);
          if (upsell) {
            setSelectedUpsell(upsell);
            openModal('funnel-edit-upsell');
          }
        }
        break;
    }
  };

  const handleCTADelete = (nodeType: string | null, nodeData: FunnelNodeData, entityId?: string | null) => {
    switch (nodeType) {
      case 'offer':
        if (offers?.data && entityId) {
          const offer = offers.data.find((o: OfferData) => String(o.id) === entityId);
          if (offer && (offer.total_sales ?? 0) < 1) {
            setSelectedOffer(offer);
            openModal('funnel-delete-offer');
          }
        }
        break;
      case 'orderBump':
        if (nodeData.type === 'orderBump' && orderBumps?.data) {
          const bump = orderBumps.data.find((b: OrderBump) => b.display_text === nodeData.label);
          if (bump) {
            setSelectedOrderBump(bump);
            openModal('funnel-delete-orderBump');
          }
        }
        break;
      case 'upsell':
        if (nodeData.type === 'upsell' && upsells?.data) {
          const upsell = upsells.data.find((u: Upsell) => u.name === nodeData.label);
          if (upsell) {
            setSelectedUpsell(upsell);
            openModal('funnel-delete-upsell');
          }
        }
        break;
    }
  };

  const handlePaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleCreateOrderBump = async (formData: any) => {
    const data = {
      productId: formData.productId,
      offer_id: formData.offer_id,
      display_text: formData.display_text,
      display_description: formData.display_description,
      call_to_action: formData.call_to_action,
    };
    try {
      await createOrderBump(data);
      closeModal('funnel-add-order-bump');
      await invalidateFunnel();
    } catch (error) {
      console.error('Error creating order bump:', error);
      throw error;
    }
  };

  const handleDeleteOffer = async () => {
    if (!selectedOffer?.id) return;
    try {
      await OffersService.deleteOffer({ offerId: selectedOffer.id, productId: productId! });
      queryClient.invalidateQueries({ queryKey: [`offers-${productId}`] });
      closeModal('funnel-delete-offer');
      await invalidateFunnel();
    } catch (error) {
      console.error('Error deleting offer:', error);
    }
  };

  const handleUpdateUpsell = async (data: any) => {
    if (selectedUpsell) {
      await updateUpsell({
        productId: productId!,
        upsellId: selectedUpsell.id,
        data,
      });
      invalidateFunnel();
    }
  };

  const handleDeleteUpsell = async (upsellId: string) => {
    try {
      await deleteUpsell({ productId: productId!, upsellId });
      closeModal('funnel-delete-upsell');
      invalidateFunnel();
    } catch (error) {
      console.error('Error deleting upsell:', error);
    }
  };

  const handleCreateUpsell = async (data: any) => {
    try {
      const result = await createUpsell({ productId: productId!, data });
      closeModal('funnel-add-upsell');

      const newPage = result?.data?.page || result?.page;
      const pending = pendingParentConnectionRef.current;

      if (pending && newPage) {
        await connectUpsells(
          pending.parentUpsellId,
          pending.direction,
          { type: 'upsell', upsellPage: newPage },
        );
        setPendingParentConnection(null);
      } else {
        invalidateFunnel();
      }
    } catch (error) {
      console.error('Error creating upsell:', error);
      throw error;
    }
  };

  const buildUpsellPayload = useCallback((
    sourceUpsell: Upsell,
    overrides: Record<string, any>,
  ) => ({
    name: sourceUpsell.name,
    funnel_type: sourceUpsell.funnel_type,
    page: sourceUpsell.page,
    offer_id: sourceUpsell.offer?.id,
    accepted_page: sourceUpsell.accepted_page ?? null,
    recused_page: sourceUpsell.recused_page ?? null,
    up_sell_display_text: sourceUpsell.up_sell_display_text,
    up_sell_button_color: sourceUpsell.up_sell_button_color,
    up_sell_display_text_color: sourceUpsell.up_sell_display_text_color,
    down_sell_display_text: sourceUpsell.down_sell_display_text,
    down_sell_button_color: sourceUpsell.down_sell_button_color,
    down_sell_display_text_color: sourceUpsell.down_sell_display_text_color,
    ...overrides,
  }), []);

  const connectUpsells = useCallback(async (
    sourceUpsellId: string,
    direction: 'accept' | 'decline',
    target: { type: 'upsell'; upsellPage: string } | { type: 'external'; url: string },
  ) => {
    const field = direction === 'accept' ? 'accepted_page' : 'recused_page';
    const url = target.type === 'upsell' ? target.upsellPage : target.url;

    const sourceUpsell = upsells?.data?.find((u: Upsell) => String(u.id) === String(sourceUpsellId));
    if (!sourceUpsell) {
      console.error('connectUpsells: upsell pai não encontrado', sourceUpsellId);
      return;
    }

    await updateUpsell({
      productId: productId!,
      upsellId: sourceUpsellId,
      data: buildUpsellPayload(sourceUpsell, { [field]: url }),
    });
    await invalidateFunnel();
  }, [productId, upsells, updateUpsell, invalidateFunnel, buildUpsellPayload]);

  const disconnectUpsell = useCallback(async (
    upsellId: string,
    direction: 'accept' | 'decline',
  ) => {
    const field = direction === 'accept' ? 'accepted_page' : 'recused_page';

    const sourceUpsell = upsells?.data?.find((u: Upsell) => String(u.id) === String(upsellId));
    if (!sourceUpsell) {
      console.error('disconnectUpsell: upsell não encontrado', upsellId);
      return;
    }

    await updateUpsell({
      productId: productId!,
      upsellId,
      data: buildUpsellPayload(sourceUpsell, { [field]: null }),
    });
    await invalidateFunnel();
  }, [productId, upsells, updateUpsell, invalidateFunnel, buildUpsellPayload]);

  const isValidConnection = useCallback((connection: Connection) => {
    const { source, sourceHandle, target } = connection;
    if (!source?.startsWith('upsell-')) return false;
    if (!target?.startsWith('upsell-')) return false;
    if (sourceHandle !== 'accept' && sourceHandle !== 'decline') return false;
    if (source === target) return false;
    return true;
  }, []);

  const handleConnect = useCallback(async (connection: Connection) => {
    const { source, sourceHandle, target } = connection;
    if (!source || !target || !sourceHandle) return;

    const sourceUpsellId = source.replace('upsell-', '');
    const targetUpsellId = target.replace('upsell-', '');
    const direction = sourceHandle as 'accept' | 'decline';

    const targetUpsell = upsells?.data?.find((u: Upsell) => String(u.id) === targetUpsellId);
    if (!targetUpsell) return;

    await connectUpsells(sourceUpsellId, direction, {
      type: 'upsell',
      upsellPage: targetUpsell.page,
    });
  }, [upsells, connectUpsells]);

  const handleReconnect = useCallback(async (oldEdge: Edge, newConnection: Connection) => {
    const oldSourceHandle = oldEdge.sourceHandle as 'accept' | 'decline';
    const oldSourceId = oldEdge.source.replace('upsell-', '');
    const newTargetId = newConnection.target?.replace('upsell-', '');

    if (!newTargetId || !oldSourceHandle) return;
    const targetUpsell = upsells?.data?.find((u: Upsell) => String(u.id) === newTargetId);
    if (!targetUpsell) return;

    await connectUpsells(oldSourceId, oldSourceHandle, {
      type: 'upsell',
      upsellPage: targetUpsell.page,
    });
  }, [upsells, connectUpsells]);

  const [connectionMenuState, setConnectionMenuState] = useState<{
    upsellId: string;
    direction: 'accept' | 'decline';
  } | null>(null);

  const [pendingParentConnection, setPendingParentConnection] = useState<{
    parentUpsellId: string;
    direction: 'accept' | 'decline';
  } | null>(null);
  const pendingParentConnectionRef = useRef(pendingParentConnection);
  useEffect(() => {
    pendingParentConnectionRef.current = pendingParentConnection;
  }, [pendingParentConnection]);

  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;

  const breadcrumbItems = [
    { text: t('common:products'), link: '/products' },
    { text: t('common:edit_product'), link: `/products/edit-product/${productId}/geral` },
    { text: t('funnel:funnel_map'), link: `/products/edit-product/${productId}/funnel` },
  ];

  if (isLoadingProductDetail || isLoadingFunnel) {
    return (
      <LayoutInside hasBgBlur items={breadcrumbItems} fullWidth>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-zinc-400">{t('funnel:loading')}</p>
          </div>
        </div>
      </LayoutInside>
    );
  }

  return (
    <LayoutInside hasBgBlur items={breadcrumbItems} fullWidth>
      <div className="relative w-full h-[calc(100vh-65px)] overflow-hidden bg-[#09090b]">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          onConnect={handleConnect}
          onReconnect={handleReconnect}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionLineStyle={{ stroke: '#10B981', strokeWidth: 2 }}
          defaultEdgeOptions={{
            type: 'animatedEdge',
            animated: true,
          }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          className="funnel-flow"
        >
          <Background color="#27272a" gap={24} size={1} />

          <Controls
            showInteractive={false}
            className="!bg-[#18181B] !border-[#27272a] !shadow-xl [&>button]:!bg-[#18181B] [&>button]:!border-[#27272a] [&>button]:!text-zinc-400 [&>button:hover]:!bg-[#27272a] [&>button]:!fill-zinc-400"
          />

          <MiniMap
            nodeColor={getMiniMapNodeColor}
            maskColor="rgba(0, 0, 0, 0.8)"
            className="!bg-[#18181B] !border-[#27272a] !shadow-xl"
            pannable
            zoomable
          />

          <Panel position="top-left" className="flex items-center gap-2">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-lg bg-[#18181B]/95 backdrop-blur-sm border border-[#27272a] px-3 py-2 shadow-xl"
            >
              <button
                onClick={() => navigate(`/products/edit-product/${productId}/geral`)}
                className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-[#27272a] transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-zinc-400" />
              </button>
              <div className="h-5 w-px bg-[#27272a]" />
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/15">
                <GitBranch className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs font-semibold text-white flex items-center gap-2">
                  {t('funnel:funnel_map')}
                  {isFetchingFunnel && !isLoadingFunnel && (
                    <span className="h-3 w-3 animate-spin rounded-full border border-primary border-t-transparent" />
                  )}
                </p>
                <p className="text-[10px] text-zinc-500">
                  {product?.name || t('funnel:product')}
                </p>
              </div>
            </motion.div>
          </Panel>

          <Panel position="top-right" className="flex items-center gap-2 max-w-[calc(100vw-280px)] sm:max-w-none">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-1 sm:gap-1.5 flex-wrap justify-end"
            >
              {disconnectedUpsells.length > 0 && (
                <button
                  onClick={() => setShowDisconnectedPanel(!showDisconnectedPanel)}
                  className={`flex items-center gap-2 rounded-lg backdrop-blur-sm border px-3 py-2 text-xs font-medium transition-all shadow-lg ${
                    showDisconnectedPanel
                      ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                      : 'bg-[#18181B]/95 border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
                  }`}
                >
                  <Unplug className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="text-[11px] font-medium hidden sm:inline whitespace-nowrap">
                    {t('funnel:disconnected_panel_btn')}
                  </span>
                  <span className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-black flex-shrink-0 ${
                    showDisconnectedPanel ? 'bg-amber-400' : 'bg-amber-500 animate-pulse'
                  }`}>
                    {disconnectedUpsells.length}
                  </span>
                </button>
              )}
              <button
                onClick={() => setShowLegend(!showLegend)}
                className={`flex items-center gap-1.5 rounded-lg backdrop-blur-sm border px-3 py-2 text-xs font-medium transition-all shadow-lg ${
                  showLegend
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'bg-[#18181B]/95 border-[#27272a] text-zinc-300 hover:bg-[#27272a]'
                }`}
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          </Panel>

          <Panel position="bottom-left">
            <AnimatePresence>
              {showLegend && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="rounded-lg bg-[#18181B]/95 backdrop-blur-sm border border-[#27272a] p-3 shadow-xl"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                    {t('funnel:legend')}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {LEGEND_KEYS.map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <item.icon
                          className="h-3.5 w-3.5"
                          style={{ color: item.color }}
                        />
                        <span className="text-[11px] text-zinc-400">
                          {t(`funnel:${item.key}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Panel>
        </ReactFlow>

        <AnimatePresence>
          {selectedNode && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="absolute top-4 right-4 z-50 w-80 rounded-xl bg-[#18181B]/98 backdrop-blur-md border border-[#27272a] shadow-2xl overflow-hidden"
            >
              <NodeDetailPanel
                nodeInfo={selectedNode}
                onClose={() => setSelectedNode(null)}
                productId={productId!}
                isProducer={isProducer}
                onOpenEditOffer={(offer) => {
                  setSelectedOffer(offer);
                  openModal('funnel-edit-offer');
                }}
                onOpenEditCheckout={(checkout) => {
                  setSelectedCheckout(checkout);
                  openModal('funnel-edit-checkout');
                }}
                onOpenEditOrderBump={(bump) => {
                  setSelectedOrderBump(bump);
                  openModal('funnel-edit-order-bump');
                }}
                onOpenEditUpsell={(upsell) => {
                  setSelectedUpsell(upsell);
                  openModal('funnel-edit-upsell');
                }}
                onDeleteOrderBump={(bump) => {
                  setSelectedOrderBump(bump);
                  openModal('funnel-delete-orderBump');
                }}
                onDeleteUpsell={(upsell) => {
                  setSelectedUpsell(upsell);
                  openModal('funnel-delete-upsell');
                }}
                offers={offers?.data}
                checkouts={checkouts?.data}
                orderBumps={orderBumps?.data}
                upsellsList={upsells?.data}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <DisconnectedUpsellsPanel
          isOpen={showDisconnectedPanel}
          onClose={() => setShowDisconnectedPanel(false)}
          disconnectedUpsells={disconnectedUpsells}
          connectedUpsells={connectedUpsells}
          onConnect={connectUpsells}
        />
      </div>

      <ConnectionMenu
        isOpen={!!connectionMenuState}
        direction={connectionMenuState?.direction || 'accept'}
        upsellId={connectionMenuState?.upsellId || ''}
        upsellName={
          upsells?.data?.find(
            (u: Upsell) => String(u.id) === connectionMenuState?.upsellId,
          )?.name || ''
        }
        availableUpsells={upsells?.data || []}
        currentConnection={(() => {
          if (!connectionMenuState) return { type: 'none' } as CurrentConnection;
          const sourceUpsell = upsells?.data?.find(
            (u: Upsell) => String(u.id) === connectionMenuState.upsellId,
          );
          if (!sourceUpsell) return { type: 'none' } as CurrentConnection;

          const field = connectionMenuState.direction === 'accept' ? 'accepted_page' : 'recused_page';
          const pageUrl = sourceUpsell[field];
          if (!pageUrl) return { type: 'none' } as CurrentConnection;

          const targetUpsell = upsells?.data?.find(
            (u: Upsell) => u.page === pageUrl && String(u.id) !== connectionMenuState.upsellId,
          );
          if (targetUpsell) {
            return {
              type: 'upsell',
              targetName: targetUpsell.name,
              targetUrl: pageUrl,
            } as CurrentConnection;
          }
          return {
            type: 'external',
            targetUrl: pageUrl,
          } as CurrentConnection;
        })()}
        onClose={() => setConnectionMenuState(null)}
        onConnect={connectUpsells}
        onDisconnect={disconnectUpsell}
        onCreateNew={() => {
          if (connectionMenuState) {
            setPendingParentConnection({
              parentUpsellId: connectionMenuState.upsellId,
              direction: connectionMenuState.direction,
            });
            setUpsellPrefilledData({
              funnel_type:
                connectionMenuState.direction === 'accept'
                  ? 'up_sell'
                  : 'down_sell',
              product_id: productId,
            });
            openModal('funnel-add-upsell');
          }
        }}
      />

      <CustomSheet
        open={modals['funnel-create-offer']}
        onClose={() => {
          closeModal('funnel-create-offer');
          invalidateFunnel();
        }}
      >
        <CreateOffer
          categoryType={product?.category?.type || 'digital'}
          productName={product?.name}
        />
      </CustomSheet>

      <CustomSheet
        open={modals['funnel-edit-offer']}
        onClose={() => {
          closeModal('funnel-edit-offer');
          invalidateFunnel();
        }}
      >
        <EditOffer
          offer={selectedOffer}
          categoryType={product?.category?.type || 'digital'}
          productName={product?.name}
        />
      </CustomSheet>

      <CustomSheet
        open={modals['funnel-create-checkout']}
        onClose={() => {
          closeModal('funnel-create-checkout');
          invalidateFunnel();
        }}
      >
        <CreateCheckout offers={offersOptions || []} modalName="funnel-create-checkout" />
      </CustomSheet>

      <CustomSheet
        open={modals['funnel-edit-checkout']}
        onClose={() => {
          closeModal('funnel-edit-checkout');
          invalidateFunnel();
        }}
      >
        <EditCheckout checkout={selectedCheckout!} offers={offersOptions || []} modalName="funnel-edit-checkout" />
      </CustomSheet>

      <CustomSheet
        open={modals['funnel-add-order-bump']}
        onClose={() => {
          closeModal('funnel-add-order-bump');
          invalidateFunnel();
        }}
      >
        <AddOrderBumpSheet
          isPending={isCreatingBump}
          onAdd={handleCreateOrderBump}
          productId={productId!}
          modalName="funnel-add-order-bump"
        />
      </CustomSheet>

      <CustomSheet
        open={modals['funnel-edit-order-bump']}
        onClose={() => {
          closeModal('funnel-edit-order-bump');
          invalidateFunnel();
        }}
      >
        <EditOrderBumpSheet
          productId={productId!}
          orderBump={selectedOrderBump!}
          modalName="funnel-edit-order-bump"
        />
      </CustomSheet>

      <CustomSheet
        open={modals['funnel-edit-upsell']}
        onClose={() => {
          closeModal('funnel-edit-upsell');
          invalidateFunnel();
        }}
      >
        <EditUpsellSheet
          productId={productId!}
          upsell={selectedUpsell}
          onUpdate={handleUpdateUpsell}
          isLoading={isUpdatingUpsell}
        />
      </CustomSheet>

      <CustomSheet
        open={modals['funnel-add-upsell']}
        onClose={() => {
          closeModal('funnel-add-upsell');
          setUpsellPrefilledData(null);
          setPendingParentConnection(null);
          invalidateFunnel();
        }}
      >
        <AddUpsellSheet
          productId={productId!}
          onAdd={handleCreateUpsell}
          isLoading={isCreatingUpsell}
          prefilledData={upsellPrefilledData}
        />
      </CustomSheet>

      <DialogDelete
        isOpen={modals['funnel-delete-upsell']}
        onClose={() => closeModal('funnel-delete-upsell')}
        title={t('upsells:excluir_upsell_titulo', 'Excluir Upsell')}
        description={t('upsells:excluir_upsell_descricao', 'Tem certeza que deseja excluir este upsell?')}
        onDelete={() => selectedUpsell && handleDeleteUpsell(selectedUpsell.id)}
      />

      <DialogDelete
        isOpen={modals['funnel-delete-orderBump']}
        onClose={() => closeModal('funnel-delete-orderBump')}
        title={t('orderBumps:delete_order_bump_title', 'Excluir Order Bump')}
        description={t('orderBumps:delete_order_bump_description', 'Tem certeza que deseja excluir este order bump?')}
        onDelete={async () => {
          if (selectedOrderBump) {
            await deleteOrderBump(selectedOrderBump.id);
            closeModal('funnel-delete-orderBump');
            invalidateFunnel();
          }
        }}
      />

      <DialogDelete
        isOpen={modals['funnel-delete-offer']}
        onClose={() => closeModal('funnel-delete-offer')}
        title={t('offers:delete_offer_title', 'Excluir Oferta')}
        description={t('offers:delete_offer_description', 'Tem certeza que deseja excluir esta oferta? Esta ação não pode ser desfeita.')}
        onDelete={handleDeleteOffer}
      />


      <SideQuickScript
        upsell={selectedUpsell}
        isOpen={!!modals['funnel-view-script']}
        onClose={() => closeModal('funnel-view-script')}
      />
    </LayoutInside>
  );
}

function NodeDetailPanel({
  nodeInfo,
  onClose,
  productId,
  isProducer,
  onOpenEditUpsell,
  onDeleteOrderBump,
  onDeleteUpsell,
  orderBumps,
  upsellsList,
}: {
  nodeInfo: SelectedNodeInfo;
  onClose: () => void;
  productId: string;
  isProducer: boolean;
  onOpenEditOffer: (offer: OfferData) => void;
  onOpenEditCheckout: (checkout: Checkout) => void;
  onOpenEditOrderBump: (bump: OrderBump) => void;
  onOpenEditUpsell: (upsell: Upsell) => void;
  onDeleteOrderBump: (bump: OrderBump) => void;
  onDeleteUpsell: (upsell: Upsell) => void;
  offers?: OfferData[];
  checkouts?: Checkout[];
  orderBumps?: OrderBump[];
  upsellsList?: Upsell[];
}) {
  const { data } = nodeInfo;
  const navigate = useNavigate();
  const { t } = useTranslation('funnel');

  const renderContent = () => {
    switch (data.type) {
      case 'productFunnel':
        return (
          <>
            <DetailHeader
              icon={<Package className="h-4 w-4 text-primary" />}
              title={data.label}
              subtitle={`${t('product')} • ${data.offers.length === 1 ? t('offers_count', { count: data.offers.length }) : t('offers_count_plural', { count: data.offers.length })}`}
              color="primary"
              onClose={onClose}
            />
            <div className="p-3 space-y-2">
              <DetailRow label={t('detail_status')} value={data.status} />
              {data.totalSales != null && <DetailRow label={t('detail_total_sales')} value={data.totalSales.toLocaleString()} />}
              {data.revenue && <DetailRow label={t('detail_total_revenue')} value={data.revenue} />}
              {data.offers.length > 0 && (
                <div className="pt-2 border-t border-[#27272a]">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-primary/70 mb-2">
                    {t('offers')} ({data.offers.length})
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {data.offers.map((offer: any) => (
                      <div key={offer.id} className="flex items-center justify-between rounded-md bg-[#27272a]/50 px-2 py-1">
                        <span className="text-[10px] text-zinc-300 truncate max-w-[120px]">{offer.name}</span>
                        <span className="text-[9px] text-cyan-400 font-medium">{offer.priceFormatted}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {isProducer && (
                <button
                  onClick={() => navigate(`/products/edit-product/${productId}/geral`)}
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-primary/10 border border-primary/20 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/20 transition-colors mt-2"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t('edit_product')}
                </button>
              )}
            </div>
          </>
        );

      case 'orderBump':
        return (
          <>
            <DetailHeader
              icon={<Gift className="h-4 w-4 text-amber-400" />}
              title={data.label}
              subtitle={data.isSecret ? `${t('order_bump_label')} (${t('secret')})` : t('order_bump_label')}
              color="amber"
              onClose={onClose}
            />
            <div className="p-3 space-y-2">
              <DetailRow label={t('detail_offer')} value={data.offerName} />
              <DetailRow label={t('detail_price')} value={data.priceFormatted} />
              <DetailRow label={t('detail_cta')} value={data.callToAction} />
              {data.isSecret && (
                <div className="flex items-center gap-1.5 rounded-md bg-violet-500/10 border border-violet-500/20 px-2.5 py-1.5">
                  <EyeOff className="h-3 w-3 text-violet-400" />
                  <span className="text-[11px] text-violet-400">{t('detail_secret_bump_active')}</span>
                </div>
              )}
              {isProducer && orderBumps && (
                <CTAActions
                  onEdit={() => {
                    const bump = orderBumps.find(b => b.display_text === data.label);
                    if (bump) onDeleteOrderBump(bump);
                  }}
                  onDelete={() => {
                    const bump = orderBumps.find(b => b.display_text === data.label);
                    if (bump) onDeleteOrderBump(bump);
                  }}
                  editLabel={t('edit_order_bump')}
                  editColor="amber"
                  canEdit={!!orderBumps.find(b => b.display_text === data.label)}
                />
              )}
            </div>
          </>
        );

      case 'upsell':
        return (
          <>
            <DetailHeader
              icon={<TrendingUp className="h-4 w-4 text-emerald-400" />}
              title={data.label}
              subtitle={data.funnelType === 'up_sell' ? t('upsell') : t('downsell')}
              color={data.funnelType === 'up_sell' ? 'emerald' : 'rose'}
              onClose={onClose}
            />
            <div className="p-3 space-y-2">
              <DetailRow label={t('detail_offer')} value={data.offerName} />
              <DetailRow label={t('detail_price')} value={data.priceFormatted} />
              <DetailRow label={t('detail_page')} value={data.page} truncate />
              {data.conversions && <DetailRow label={t('detail_sales')} value={data.conversions.toString()} />}
              {data.revenue && <DetailRow label={t('detail_revenue')} value={data.revenue} />}
              {isProducer && upsellsList && (
                <CTAActions
                  onEdit={() => {
                    const upsell = upsellsList.find(u => u.name === data.label);
                    if (upsell) onOpenEditUpsell(upsell);
                  }}
                  onDelete={() => {
                    const upsell = upsellsList.find(u => u.name === data.label);
                    if (upsell) onDeleteUpsell(upsell);
                  }}
                  editLabel={t('edit_upsell')}
                  editColor="emerald"
                  canEdit={!!upsellsList.find(u => u.name === data.label)}
                />
              )}
            </div>
          </>
        );

      case 'completion':
        return (
          <>
            <DetailHeader
              icon={<CheckCircle2 className="h-4 w-4 text-emerald-400" />}
              title={t('purchase_completion')}
              subtitle={t('detail_end_of_funnel')}
              color="emerald"
              onClose={onClose}
            />
            <div className="p-3 space-y-2">
              <DetailRow label={t('detail_origin')} value={data.source} />
              <div className="rounded-md bg-emerald-500/5 border border-emerald-500/10 px-2.5 py-2">
                <p className="text-[10px] text-emerald-400/80">
                  {t('detail_completion_desc')}
                </p>
              </div>
            </div>
          </>
        );

      case 'externalRedirect':
        return (
          <>
            <DetailHeader
              icon={<ExternalLink className="h-4 w-4 text-blue-400" />}
              title={t('external_redirect')}
              subtitle={t('detail_external_page')}
              color="blue"
              onClose={onClose}
            />
            <div className="p-3 space-y-2">
              <DetailRow label={t('detail_origin')} value={data.source} />
              <DetailRow label={t('detail_url')} value={data.url} truncate />
              <div className="rounded-md bg-blue-500/5 border border-blue-500/10 px-2.5 py-2">
                <p className="text-[10px] text-blue-400/80">
                  {t('detail_redirect_desc')}
                </p>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return <>{renderContent()}</>;
}

function CTAActions({
  onEdit,
  onDelete,
  editLabel,
  editColor,
  canEdit,
}: {
  onEdit: () => void;
  onDelete?: () => void;
  editLabel: string;
  editColor: string;
  canEdit: boolean;
}) {
  if (!canEdit) return null;

  return (
    <div className="pt-2 flex items-center gap-1.5">
      <button
        onClick={onEdit}
        className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-${editColor}-500/10 border border-${editColor}-500/20 px-3 py-2 text-xs font-medium text-${editColor}-400 hover:bg-${editColor}-500/20 transition-colors`}
      >
        <Edit className="h-3 w-3" />
        {editLabel}
      </button>
      {onDelete && (
        <button
          onClick={onDelete}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors flex-shrink-0"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function DetailHeader({
  icon,
  title,
  subtitle,
  color,
  onClose,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-3 border-b border-[#27272a]">
      <div className="flex items-center gap-2.5">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-lg bg-${color}-500/15 ring-1 ring-${color}-500/30`}
        >
          {icon}
        </div>
        <div>
          <p className="text-sm font-semibold text-white truncate max-w-[180px]">
            {title}
          </p>
          <p className="text-[10px] text-zinc-500">{subtitle}</p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-[#27272a] transition-colors"
      >
        <X className="h-3.5 w-3.5 text-zinc-500" />
      </button>
    </div>
  );
}

function DetailRow({
  label,
  value,
  truncate,
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] text-zinc-500 flex-shrink-0">{label}</span>
      <span
        className={`text-[11px] text-zinc-300 text-right ${
          truncate ? 'truncate max-w-[170px]' : ''
        }`}
        title={truncate ? value : undefined}
      >
        {value}
      </span>
    </div>
  );
}
